/**
 * GUI 삼각형 애니메이션 훅
 *
 * guiTriangleStore의 enabled 상태를 모니터링하여
 * GSAP 애니메이션으로 animationProgress를 업데이트합니다.
 *
 * 앱 최상위에서 한 번만 호출해야 합니다.
 */
import { useEffect, useRef } from "react";
import gsap from "gsap";
import { useGUITriangleStore, type CornerKey } from "@/stores/guiTriangleStore";

interface UseGUITriangleAnimationOptions {
  /** 애니메이션 지속 시간 (초) */
  duration?: number;
  /** GSAP 이징 함수 */
  ease?: string;
}

/**
 * GUI 삼각형 애니메이션 훅
 * 스토어의 enabled 상태 변경을 감지하여 애니메이션 진행도를 업데이트합니다.
 */
export function useGUITriangleAnimation({
  duration = 0.8,
  ease = "power2.out",
}: UseGUITriangleAnimationOptions = {}) {
  const corners = useGUITriangleStore((state) => state.corners);
  const setAnimationProgress = useGUITriangleStore(
    (state) => state.setAnimationProgress
  );

  // 각 코너별 트윈 참조
  const tweensRef = useRef<Record<CornerKey, gsap.core.Tween | null>>({
    topLeft: null,
    topRight: null,
    bottomLeft: null,
    bottomRight: null,
  });

  // 현재 애니메이션 값 (GSAP 타겟용)
  const progressRef = useRef<Record<CornerKey, { value: number }>>({
    topLeft: { value: 1 },
    topRight: { value: 1 },
    bottomLeft: { value: 1 },
    bottomRight: { value: 1 },
  });

  // 이전 enabled 상태 추적
  const prevEnabledRef = useRef<Record<CornerKey, boolean>>({
    topLeft: corners.topLeft.enabled,
    topRight: corners.topRight.enabled,
    bottomLeft: corners.bottomLeft.enabled,
    bottomRight: corners.bottomRight.enabled,
  });

  // 개별 코너 애니메이션 함수
  const animateCorner = (corner: CornerKey, targetValue: number) => {
    // 기존 트윈 정리
    if (tweensRef.current[corner]) {
      tweensRef.current[corner]?.kill();
    }

    // 새 트윈 생성
    tweensRef.current[corner] = gsap.to(progressRef.current[corner], {
      value: targetValue,
      duration,
      ease,
      onUpdate: () => {
        setAnimationProgress(corner, progressRef.current[corner].value);
      },
    });
  };

  // 각 코너의 enabled 변경 감지
  useEffect(() => {
    const cornerKeys: CornerKey[] = [
      "topLeft",
      "topRight",
      "bottomLeft",
      "bottomRight",
    ];

    cornerKeys.forEach((corner) => {
      const prevEnabled = prevEnabledRef.current[corner];
      const currentEnabled = corners[corner].enabled;

      if (prevEnabled !== currentEnabled) {
        // enabled 변경됨 → 애니메이션 시작
        animateCorner(corner, currentEnabled ? 0 : 1);
        prevEnabledRef.current[corner] = currentEnabled;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    corners.topLeft.enabled,
    corners.topRight.enabled,
    corners.bottomLeft.enabled,
    corners.bottomRight.enabled,
  ]);

  // 마운트 시 초기 애니메이션 (enabled인 코너들만 stagger로 열기)
  useEffect(() => {
    const cornerKeys: CornerKey[] = [
      "topLeft",
      "topRight",
      "bottomLeft",
      "bottomRight",
    ];
    const delays = [0, 0.1, 0.15, 0.2];

    cornerKeys.forEach((corner, index) => {
      if (corners[corner].enabled) {
        setTimeout(() => {
          animateCorner(corner, 0);
        }, delays[index] * 1000);
      }
    });

    // Cleanup
    return () => {
      Object.values(tweensRef.current).forEach((tween) => tween?.kill());
    };
    // 마운트 시에만 실행
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
